import { z } from 'zod';
import {
  isoDateNullableSchema,
  isoDateSchema,
  montantNullableSchema,
  nullableText,
  paginationSchema,
  round2,
} from './common.ts';
import type { OeilOrdonnance } from './ordonnance.ts';

export const COMMANDE_STATUTS = ['EN_COURS', 'LIVREE', 'ANNULEE'] as const;
export const commandeStatutSchema = z.enum(COMMANDE_STATUTS);
export type CommandeStatut = z.infer<typeof commandeStatutSchema>;

export const COMMANDE_STATUT_LABELS: Record<CommandeStatut, string> = {
  EN_COURS: 'En cours',
  LIVREE: 'Livrée',
  ANNULEE: 'Annulée',
};

/**
 * Transitions autorisées — règle 10. `ANNULEE` est terminal.
 * Contrairement aux factures, une commande reste modifiable dans tous les
 * statuts : c'est le cahier de travail, pas un document légal.
 */
export const TRANSITIONS_STATUT: Record<CommandeStatut, readonly CommandeStatut[]> = {
  EN_COURS: ['LIVREE', 'ANNULEE'],
  LIVREE: ['ANNULEE'],
  ANNULEE: [],
};

export function peutTransitionner(depuis: CommandeStatut, vers: CommandeStatut): boolean {
  if (depuis === vers) return true;
  return TRANSITIONS_STATUT[depuis].includes(vers);
}

/** Prix d'une commande, les valeurs absentes comptant pour 0 — règle 8. */
export type PrixCommande = {
  prix_verre?: number | null;
  prix_monture?: number | null;
  prix_lentilles?: number | null;
};

/** Somme des trois lignes de prix. Sert à préremplir et à contrôler `total`. */
export function calculerTotalCommande(prix: PrixCommande): number {
  return round2((prix.prix_verre ?? 0) + (prix.prix_monture ?? 0) + (prix.prix_lentilles ?? 0));
}

/** Tolérance d'un centime : en dessous, `total` et la somme sont réputés égaux. */
export const TOLERANCE_TOTAL = 0.01;

/** Vrai si le total saisi s'écarte de la somme des prix — règle 8. */
export function totalDiverge(total: number, prix: PrixCommande): boolean {
  return Math.abs(round2(total) - calculerTotalCommande(prix)) > TOLERANCE_TOTAL + Number.EPSILON;
}

/** Reste dû — règle 9. Peut être négatif (trop-perçu), ce n'est pas une erreur. */
export function calculerReste(total: number, avance: number): number {
  return round2(total - avance);
}

export const ordonnanceCommandeSchema = z.object({
  od_raw: nullableText,
  og_raw: nullableText,
  addition: nullableText,
});

/**
 * Mesures de montage — ce qu'il faut au laboratoire, en plus de la correction.
 *
 * Volontairement du texte libre, comme l'ordonnance : « 31,5 » est recopié tel
 * qu'écrit, jamais converti en nombre. Rien n'est refusé, rien n'est corrigé
 * d'office ; ces champs ne lèvent donc pas non plus le drapeau « à vérifier »,
 * qui appartient à l'analyse de l'ordonnance importée.
 */
export const mesuresMontageSchema = z.object({
  /** Écart pupillaire, en millimètres, par œil. */
  ecart_od: nullableText,
  ecart_og: nullableText,
  /** Hauteur de montage : du bas du calibre au centre de la pupille. */
  hauteur_od: nullableText,
  hauteur_og: nullableText,
  /** Largeur de boîte du calibre. */
  largeur_od: nullableText,
  largeur_og: nullableText,
  /** Plus grande diagonale : c'est elle qui décide du diamètre du palet. */
  diagonale_od: nullableText,
  diagonale_og: nullableText,
  /** Longueur du couloir d'un progressif (14, 18…). */
  progression_od: nullableText,
  progression_og: nullableText,
});

/**
 * Les cinq cotes, dans l'ordre où l'opticien les relève : l'œil d'abord
 * (écart, hauteur), puis le calibre (largeur, diagonale), puis le verre
 * (progression). Cet ordre pilote l'affichage — le changer ici le change
 * partout, plutôt que dans chaque écran.
 */
export const COTES_MONTAGE = [
  { cle: 'ecart', libelle: 'Écart pupillaire', abrege: 'Écart' },
  { cle: 'hauteur', libelle: 'Hauteur de montage', abrege: 'Hauteur' },
  { cle: 'progression', libelle: 'Hauteur de progression', abrege: 'Progr.' },
  { cle: 'largeur', libelle: 'Largeur', abrege: 'Largeur' },
  { cle: 'diagonale', libelle: 'Diagonale', abrege: 'Diag.' },
] as const;

export type CoteMontage = (typeof COTES_MONTAGE)[number]['cle'];

/**
 * Matière de la monture — se relève avec les cotes, pour la même raison
 * qu'elles : le laboratoire en a besoin pour usiner. Le biseau d'un acétate
 * n'est pas celui d'un métal cerclé, et le palet utile n'est pas le même.
 *
 * Deux valeurs seulement, celles qu'il dicte au téléphone. Une liste fermée
 * plutôt qu'un champ libre : la matière sert à trier et à compter, ce que
 * « plastique », « Plastique » et « acétate » écrits au fil des ans rendraient
 * impossible. Ajouter le nylor ou le percé, plus tard, tient en une ligne ici.
 */
export const MATIERES_MONTURE = ['PLASTIQUE', 'METAL'] as const;
export const matiereMontureSchema = z.enum(MATIERES_MONTURE);
export type MatiereMonture = (typeof MATIERES_MONTURE)[number];

export const MATIERE_MONTURE_LABELS: Record<MatiereMonture, string> = {
  PLASTIQUE: 'Plastique',
  METAL: 'Métal',
};

/**
 * Matière facultative, et vide ramenée à `null`.
 *
 * Le champ n'a de sens que pour une commande qui comporte une monture : une
 * commande de verres seuls, de lentilles ou de produit d'entretien n'a rien à
 * y mettre, et l'exiger obligerait à cocher une case fausse. Une valeur hors
 * liste — un ancien import, un client qui bricole l'API — est ramenée à `null`
 * plutôt que de faire échouer l'enregistrement de toute la commande.
 */
export const matiereMontureNullableSchema = z
  .union([matiereMontureSchema, z.string(), z.null(), z.undefined()])
  .transform((v) =>
    typeof v === 'string' && (MATIERES_MONTURE as readonly string[]).includes(v)
      ? (v as MatiereMonture)
      : null,
  );

export type MesuresMontage = {
  ecart_od: string | null;
  ecart_og: string | null;
  hauteur_od: string | null;
  hauteur_og: string | null;
  largeur_od: string | null;
  largeur_og: string | null;
  diagonale_od: string | null;
  diagonale_og: string | null;
  progression_od: string | null;
  progression_og: string | null;
};

/** Vrai si aucune cote n'est renseignée — sert aux écrans, pas à la validation. */
export function mesuresVides(m: MesuresMontage): boolean {
  return COTES_MONTAGE.every((c) => !m[`${c.cle}_od`] && !m[`${c.cle}_og`]);
}

/**
 * Vrai si rien du tout n'a été relevé pour le montage — ni cote, ni matière.
 *
 * Distinct de `mesuresVides` à dessein : la matière ne participe pas au choix
 * de la commande dont on reprend l'écart pupillaire (une commande qui ne
 * porterait qu'« acétate » n'apprend rien sur le visage), mais elle suffit à
 * justifier l'affichage du bloc. Sans cette nuance, une monture dont on a noté
 * la matière sans prendre les cotes ne s'afficherait nulle part.
 */
export function montageVide(c: MesuresMontage & { matiere_monture: string | null }): boolean {
  return mesuresVides(c) && !c.matiere_monture;
}

export const commandeInputSchema = z.object({
  numero_bon: nullableText,
  date_commande: isoDateSchema,
  client_id: nullableText,
  client_nom: z.string().trim().min(1, 'Le nom du client est obligatoire').max(200),
  client_tel: nullableText,

  od_raw: nullableText,
  og_raw: nullableText,
  addition: nullableText,

  /* Mesures de montage et provenance de l'ordonnance.
   *
   * `.optional()` comme les rattachements au stock, et pour la même raison :
   * ces champs peuvent être absents de la charge utile, pas seulement vides.
   * Une ligne importée n'en porte aucun, et une commande de lentilles ou de
   * produit d'entretien n'a rien à y mettre. Omis, ils restent nuls ; envoyés
   * vides, `nullableText` les ramène à null. */
  ecart_od: nullableText.optional(),
  ecart_og: nullableText.optional(),
  hauteur_od: nullableText.optional(),
  hauteur_og: nullableText.optional(),
  largeur_od: nullableText.optional(),
  largeur_og: nullableText.optional(),
  diagonale_od: nullableText.optional(),
  diagonale_og: nullableText.optional(),
  progression_od: nullableText.optional(),
  progression_og: nullableText.optional(),
  /** Matière de la monture — se relève avec les cotes, sert au laboratoire. */
  matiere_monture: matiereMontureNullableSchema.optional(),
  prescripteur: nullableText.optional(),
  /** Date de la prescription — c'est elle qui ouvre le délai AMO, pas la commande. */
  date_ordonnance: isoDateNullableSchema.optional(),

  /* Référence produit facultative pour chaque poste : renseignée quand
   * l'article vient du stock, nulle pour une saisie libre ou un import. C'est
   * elle qui décide de ce qui sort du stock à la livraison. */
  verre_product_id: z.string().nullable().optional(),
  monture_product_id: z.string().nullable().optional(),
  lentilles_product_id: z.string().nullable().optional(),
  type_verre: nullableText,
  prix_verre: montantNullableSchema,
  monture: nullableText,
  prix_monture: montantNullableSchema,
  lentilles: nullableText,
  prix_lentilles: montantNullableSchema,

  societe: nullableText,
  /** Omis => calculé depuis la somme des prix. Fourni => stocké tel quel (règle 8). */
  total: montantNullableSchema.optional(),
  statut: commandeStatutSchema.default('EN_COURS'),
});
export type CommandeInput = z.infer<typeof commandeInputSchema>;

/** Toute commande reste modifiable, quel que soit son statut (règle 10). */
export const commandeUpdateSchema = commandeInputSchema.partial();
export type CommandeUpdate = z.infer<typeof commandeUpdateSchema>;

export const commandeStatutInputSchema = z.object({
  statut: commandeStatutSchema,
});

export const commandeListQuerySchema = paginationSchema.extend({
  q: z.string().trim().optional(),
  from: isoDateSchema.optional(),
  to: isoDateSchema.optional(),
  societe: z.string().trim().optional(),
  statut: commandeStatutSchema.optional(),
  non_soldees: z.coerce.boolean().optional(),
  a_verifier: z.coerce.boolean().optional(),
  client_id: z.string().trim().optional(),
  tri: z.enum(['date_commande', 'client_nom', 'total', 'reste']).default('date_commande'),
  sens: z.enum(['asc', 'desc']).default('desc'),
});
export type CommandeListQuery = z.infer<typeof commandeListQuerySchema>;

/** Déduction de stock rattachée à une commande — règle 11. */
export const deduireStockSchema = z.object({
  product_id: z.string().min(1, 'Sélectionnez un produit'),
  quantite: z.coerce.number().int().min(1, 'La quantité doit être au moins 1'),
  note: nullableText.optional(),
});
export type DeduireStockInput = z.infer<typeof deduireStockSchema>;

export type Commande = {
  id: string;
  numero_bon: string | null;
  date_commande: string;
  client_id: string | null;
  client_nom: string;
  client_tel: string | null;

  od_raw: string | null;
  og_raw: string | null;
  od: OeilOrdonnance;
  og: OeilOrdonnance;
  addition: string | null;

  ecart_od: string | null;
  ecart_og: string | null;
  hauteur_od: string | null;
  hauteur_og: string | null;
  largeur_od: string | null;
  largeur_og: string | null;
  diagonale_od: string | null;
  diagonale_og: string | null;
  progression_od: string | null;
  progression_og: string | null;
  matiere_monture: MatiereMonture | null;
  prescripteur: string | null;
  date_ordonnance: string | null;

  verre_product_id: string | null;
  monture_product_id: string | null;
  lentilles_product_id: string | null;
  type_verre: string | null;
  prix_verre: number | null;
  monture: string | null;
  prix_monture: number | null;
  lentilles: string | null;
  prix_lentilles: number | null;

  societe: string | null;
  total: number;
  statut: CommandeStatut;
  a_verifier: boolean;
  source: 'APP' | 'IMPORT';
  import_raw: Record<string, unknown> | null;
  facture_id: string | null;

  livree_at: string | null;
  annulee_at: string | null;
  created_at: string;
  updated_at: string | null;

  /** Calculés, jamais stockés (règle 9). */
  avance: number;
  reste: number;
};
